import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import SignUpModal from '../components/auth/SignUpModal';
import SignInModal from '../components/auth/SignInModal';

import InteractiveFeaturesSection from '../components/InteractiveFeaturesSection';
import { Helmet } from 'react-helmet-async';

import HomeChartStudio from './studio/HomeChartStudio';

const IS_PROD = window.location.hostname !== 'localhost'
  && window.location.hostname !== '127.0.0.1';

// ── VideoShowcaseCard — plays on scroll into view, pauses on scroll away ──
const VideoShowcaseCard = ({ video, poster, label, accentColor, onClick }) => {
  const videoRef = useRef(null);
  const wrapRef  = useRef(null);

  useEffect(() => {
    const el  = videoRef.current;
    const wrap = wrapRef.current;
    if (!el || !wrap) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.play().catch(() => {});
        } else {
          el.pause();
        }
      },
      { threshold: 0.35 }
    );
    observer.observe(wrap);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={wrapRef}
      onClick={onClick}
      style={{
        position: 'relative',
        borderRadius: 16,
        overflow: 'hidden',
        aspectRatio: '16/9',
        cursor: 'pointer',
        background: '#0f1020',
        boxShadow: '0 4px 32px rgba(0,0,0,0.45)',
        transition: 'transform 0.22s, box-shadow 0.22s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'scale(1.025)';
        e.currentTarget.style.boxShadow = `0 12px 48px rgba(0,0,0,0.6), 0 0 0 2px ${accentColor}55`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.boxShadow = '0 4px 32px rgba(0,0,0,0.45)';
      }}
    >
      {/* Video */}
      <video
        ref={videoRef}
        src={video}
        poster={poster}
        muted
        loop
        playsInline
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />

      {/* Bottom overlay: always visible label */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.12) 48%, transparent 100%)',
        display: 'flex', flexDirection: 'column',
        justifyContent: 'flex-end', padding: '18px 20px',
        transition: 'opacity 0.2s',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{
            fontSize: 13, fontWeight: 800, color: '#fff',
            letterSpacing: '.04em', textShadow: '0 1px 4px rgba(0,0,0,0.5)',
          }}>
            {label}
          </span>
          {/* Try it pill */}
          <span style={{
            fontSize: 11, fontWeight: 800, color: '#fff',
            background: accentColor,
            borderRadius: 99, padding: '4px 12px',
            letterSpacing: '.05em', textTransform: 'uppercase',
            boxShadow: `0 2px 12px ${accentColor}99`,
          }}>
            Try it →
          </span>
        </div>
      </div>
    </div>
  );
};


const HomePage = ({ initialChartType } = {}) => {
  const [activeTab, setActiveTab] = useState('line');
  const [activeChartType, setActiveChartType] = useState('bar');
  const [showSignUp, setShowSignUp] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const { currentUser } = useAuth();
  
  const primaryRef = useRef(null);
  const secondaryRef = useRef(null);

  useEffect(() => {
    // Track page view when homepage loads
    if (IS_PROD && window.gtag) {
      window.gtag("event", "page_view", {
        page_title: "Chart2Video Home",
        page_path: "/"
      });
    }
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Google analytics event
    if (IS_PROD && window.gtag) {
      window.gtag("event", "chart_selected", {
        event_category: "User Interaction",
        event_label: tab,
      });
    }
  };

  
  const handleCTAClick = () => {
    if (IS_PROD && window.gtag) {
      window.gtag("event", "cta_clicked", {
        event_category: "User Interaction",
        event_label: "Start Creating Now",
        position: "hero_section"
      });
    }
  };

  const handleSeeHowItWorksClick = () => {
    if (IS_PROD && window.gtag) {
      window.gtag("event", "cta_clicked", {
        event_category: "User Interaction",
        event_label: "See How It Works",
        position: "hero_section"
      });
    }
  };


  // Image/video error handler with proper fallbacks
  // Image/video error handler with proper fallbacks
  // Image/video error handler with proper fallbacks
  const handleImageError = (e, item) => {
    try {
      // Check if item exists and has necessary properties
      if (!item) {
        console.warn("Item is undefined in handleImageError");
        return;
      }
      
      // If this is a video element that failed, fallback to image
      if (e.target && e.target.tagName && e.target.tagName.toLowerCase() === 'video') {
        if (item.image) {
          // Create image element
          const imgElement = document.createElement('img');
          imgElement.src = item.image;
          imgElement.alt = item.name || 'Chart item';
          imgElement.className = "w-full h-full object-cover";
          imgElement.onerror = (event) => handleImageError(event, item);
          
          // Check if parent node exists and is valid
          if (e.target.parentNode) {
            try {
              // Safely clear and append
              e.target.parentNode.innerHTML = '';
              e.target.parentNode.appendChild(imgElement);
            } catch (appendError) {
              console.warn("Failed to append image element:", appendError);
              // Alternative approach if innerHTML/appendChild fails
              if (e.target.parentNode) {
                e.target.src = item.image; // Just try to set the src instead
              }
            }
          } else {
            console.warn("Parent node not found for video element");
            // Just try to replace the src if possible
            if (e.target) {
              e.target.src = item.image;
            }
          }
        }
        return;
      }
      
      // For regular image fallbacks
      if (!e.target) {
        console.warn("Target element not found in handleImageError");
        return;
      }
      
      if (item.fallbackImages && item.fallbackImages.length > 0) {
        const currentSrc = e.target.src;
        
        // If current image is in fallbacks, try the next one
        const currentIndex = item.fallbackImages.findIndex(src => src === currentSrc);
        
        if (currentIndex === -1) {
          // First error - try first fallback
          e.target.src = item.fallbackImages[0];
        } else if (currentIndex < item.fallbackImages.length - 1) {
          // Try next fallback image
          e.target.src = item.fallbackImages[currentIndex + 1];
        } else {
          // All fallbacks failed, use placeholder with safe color reference
          const colorCode = item.color ? item.color.replace('#', '') : 'CCCCCC';
          e.target.src = `https://via.placeholder.com/50/${colorCode}/FFFFFF/?text=${(item.name || 'X').charAt(0)}`;
        }
      } else {
        // No fallbacks defined, use generic placeholder with safe color reference
        const colorCode = item.color ? item.color.replace('#', '') : 'CCCCCC';
        e.target.src = `https://via.placeholder.com/50/${colorCode}/FFFFFF/?text=${(item.name || 'X').charAt(0)}`;
      }
    } catch (error) {
      console.error("Error in handleImageError:", error);
      // Last resort fallback
      try {
        if (e.target) {
          e.target.src = "https://via.placeholder.com/50/CCCCCC/FFFFFF/?text=!";
        }
      } catch (finalError) {
        console.error("Failed to set fallback image:", finalError);
      }
    }
  };

  // Chart types with updated list
  const chartTypes = [
   
    {
      type: 'line',
      title: 'Line Charts',
      description: 'Ideal for showing continuous data or trends over periods of time.',
      image: '/images/homepage_charts_section/line.jpg',
      videoUrl: '/images/homepage_charts_section/line.mp4',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
        </svg>
      )
    },
    {
      type: 'bar',
      title: 'Bar Charts',
      description: 'Perfect for comparing values across categories or showing trends over time.',
      videoUrl: '/images/homepage_charts_section/bar.mp4',
      image: '/images/homepage_charts_section/bar.jpg',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      )
    },
    {
      type: 'pie',
      title: 'Pie Charts',
      description: 'Best for showing proportions and percentages of a whole.',
      image: '/images/homepage_charts_section/pie.jpg',
      videoUrl: '/images/homepage_charts_section/pie.mp4',
      //the issue was with the videUrl not being found
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
        </svg>
      )
    },
    {
      type: 'heatmap',
      title: 'Heat Maps',
      description: 'Visualize data density and patterns through color variations across a matrix.',
      image: '/images/homepage_charts_section/heat.jpg',
      videoUrl: '/images/homepage_charts_section/heat.mp4',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      )
    },
    {
      type: 'bubble',
      title: 'Bubble Charts',
      description: 'Display three dimensions of data through position and size of circles.',
      image: '/images/homepage_charts_section/bubble.jpg',
      videoUrl: '/images/homepage_charts_section/bubble.mp4',
      
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16a5 5 0 01-.916-9.916 5.002 5.002 0 019.832 0A5.002 5.002 0 0116 16m-7-5a2 2 0 104 0 2 2 0 00-4 0z" />
        </svg>
      )
    },
    // {
    //   type: 'bigdata',
    //   title: 'Big Data Visualizations',
    //   description: 'Handle large datasets with optimized performance and interactive exploration.',
    //   image: '/images/homepage_charts_section/bigdata_homepage.jpg',
    //   videoUrl: '/images/homepage_charts_section/bigdata_homepage.mp4',
    //   icon: (
    //     <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    //       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    //     </svg>
    //   )
    // }
  ];


  function ChartVideoWithFallback({ chart }) {
    const [primaryActive, setPrimaryActive] = useState(true);
  
    // Set up the crossfade effect
    useEffect(() => {
      if (!chart.videoUrl) return;
      
      const primary = primaryRef.current;
      const secondary = secondaryRef.current;
      
      if (!primary || !secondary) return;
      
      const handleTimeUpdate = () => {
        // When primary video is about to end, start fading to secondary
        if (primaryActive && primary.currentTime > primary.duration - 0.3) {
          // Make sure secondary is playing and ready to take over
          if (secondary.paused) {
            secondary.currentTime = 0;
            secondary.play().catch(e => console.error("Playback prevented", e));
          }
          
          // Crossfade
          primary.style.opacity = "0";
          secondary.style.opacity = "1";
          setPrimaryActive(false);
        } 
        // When secondary video is about to end, start fading to primary
        else if (!primaryActive && secondary.currentTime > secondary.duration - 0.3) {
          // Make sure primary is playing and ready to take over
          if (primary.paused) {
            primary.currentTime = 0;
            primary.play().catch(e => console.error("Playback prevented", e));
          }
          
          // Crossfade
          secondary.style.opacity = "0";
          primary.style.opacity = "1";
          setPrimaryActive(true);
        }
      };
      
      // Add event listeners to both videos
      primary.addEventListener('timeupdate', handleTimeUpdate);
      secondary.addEventListener('timeupdate', handleTimeUpdate);
      
      // Initial setup
      primary.play().catch(e => console.error("Initial playback prevented", e));
      primary.style.opacity = "1";
      secondary.style.opacity = "0";
      
      return () => {
        primary.removeEventListener('timeupdate', handleTimeUpdate);
        secondary.removeEventListener('timeupdate', handleTimeUpdate);
      };
    }, [chart.videoUrl, primaryActive]);
  }
  
  

  return (
    <div className="bg-white min-h-screen">
      <Helmet>
        <title>Animated Chart Maker - Create Chart Videos Free | Chart2Video</title>
        <meta name="description" content="Create animated bar charts, line charts, pie charts and more. Transform your data into professional video visualizations in minutes. Free online chart to video maker." />
        <link rel="canonical" href="https://www.chart2video.com/" />
        <meta property="og:title" content="Animated Chart Maker | Chart2Video" />
        <meta property="og:description" content="Turn data into stunning animated chart videos in seconds. Free chart to video converter." />
        <meta property="og:url" content="https://www.chart2video.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.chart2video.com/logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Animated Chart Maker | Chart2Video" />
        <meta name="twitter:description" content="Turn data into stunning animated chart videos in seconds. Free chart to video converter." />
        <meta name="twitter:image" content="https://www.chart2video.com/logo.png" />
      </Helmet>
      {/* Hero Section - Keep dark theme */}
      <section className="bg-gradient-to-br from-[#12152e] to-[#0c0e1a] px-4 py-12 md:py-16 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
            <div className="md:w-1/2">
              <motion.h1 
                style={{ fontSize:"clamp(2rem,5vw,3.25rem)", fontWeight:900, marginBottom:16, lineHeight:1.12, letterSpacing:"-0.025em", background:"linear-gradient(110deg,#f5a623,#f26419,#f04e6b)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                Transform Your Data Into Stunning Animated Videos
              </motion.h1>
              
              <motion.p 
                className="text-xl text-gray-300 mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                Create professional-quality data visualization videos in minutes without video editing skills. AI Chart Maker Coming Soon!
              </motion.p>
              
              <motion.div 
                className="flex flex-wrap gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <a 
                  href="#create"
                  className="btn-primary"
                  onClick={handleCTAClick}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                  Start Creating Now
                </a>
                <a 
                  href="#create" 
                  className="btn-ghost"
                  onClick={handleSeeHowItWorksClick}
                >
                  FREE
                </a>
              </motion.div>
            </div>
            
            <div className="md:w-1/2">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, delay: 0.3 }}
                className="rounded-xl overflow-hidden shadow-2xl border border-gray-800"
              >
                    <video
                        src="/images/homepage_charts_section/bar.mp4"
                        alt="Chart animation demonstration"
                        className="w-full h-auto"
                        autoPlay
                        loop
                        muted
                        playsInline
                    />
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Replace the static features section with the interactive one */}
      <InteractiveFeaturesSection />
 
      
{/* Embedded Chart Studio — full width, no navigation needed */}
      <section id="create" className="bg-white border-b border-gray-200">
        <HomeChartStudio initialType={initialChartType} />
      </section>



      



      {/* DataBlog Section */}
      <section style={{ background: '#f8f7ff', padding: '20px 20px' }}>
        <div className="max-w-7xl mx-auto">
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 40, flexWrap: 'wrap', gap: 16 }}>
            <div>
              <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--c2v-primary)', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 8, display: 'block' }}>
                DataBlog
              </span>
              <h2 style={{ fontSize: 32, fontWeight: 900, color: '#111827', margin: 0, letterSpacing: '-0.02em' }}>
                Data stories worth reading
              </h2>
            </div>
            <Link to="/datablog" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: 'var(--c2v-primary)', textDecoration: 'none', border: '1.5px solid var(--c2v-primary-mid)', borderRadius: 8, padding: '8px 16px', background: '#fff', transition: 'all 0.15s' }}>
              View all articles
              <svg xmlns="http://www.w3.org/2000/svg" style={{ width:14, height:14 }} viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                to: '/datablog/simple-data-visualization-tool',
                tag: 'Data Visualization',
                tagColor: '#7c3aed',
                tagBg: '#ede9fe',
                title: "The Simplest Data Visualization Tool You're Not Using",
                desc: 'Why simple, animated charts outperform complex dashboards for storytelling and audience engagement.',
              },
              {
                to: '/datablog/ms-word-graph',
                tag: 'Tutorials',
                tagColor: '#0891b2',
                tagBg: '#e0f2fe',
                title: 'How to Make a Graph in MS Word (And When to Go Beyond It)',
                desc: 'A practical guide to Word charts — and why animated video exports make your data land harder.',
              },
              {
                to: '/datablog/is-excel-a-visualization-platform',
                tag: 'Analysis',
                tagColor: '#059669',
                tagBg: '#d1fae5',
                title: 'Is Excel Actually a Data Visualization Platform?',
                desc: 'Excel can make charts, but is it built for visualization? We break down where it helps and where it limits you.',
              },
            ].map(({ to, tag, tagColor, tagBg, title, desc }) => (
              <Link key={to} to={to} style={{ textDecoration: 'none', display: 'block' }}>
                <div style={{
                  background: '#fff',
                  border: '1.5px solid #e8e4ff',
                  borderRadius: 14,
                  padding: '28px 24px',
                  height: '100%',
                  transition: 'all 0.2s',
                  boxSizing: 'border-box',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = 'var(--c2v-primary)';
                  e.currentTarget.style.boxShadow = '0 8px 30px rgba(104,84,224,0.12)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = '#e8e4ff';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  <span style={{ fontSize: 10, fontWeight: 800, color: tagColor, background: tagBg, borderRadius: 5, padding: '3px 8px', letterSpacing: '.07em', textTransform: 'uppercase' }}>
                    {tag}
                  </span>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: '14px 0 10px', lineHeight: 1.45 }}>{title}</h3>
                  <p style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.65, margin: '0 0 18px' }}>{desc}</p>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: 'var(--c2v-primary)' }}>
                    Read article
                    <svg xmlns="http://www.w3.org/2000/svg" style={{ width:12, height:12 }} viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Example Showcase — commented out, will update with new examples later ── */}
      {/*
      <section style={{ background: '#fff', borderTop: '1px solid var(--c2v-border)', borderBottom: '1px solid var(--c2v-border)', padding: '24px 24px' }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
            <div>
              <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--c2v-primary)', letterSpacing: '.12em', textTransform: 'uppercase' }}>Live examples</span>
              <p style={{ margin: '3px 0 0', fontSize: 14, fontWeight: 700, color: 'var(--c2v-text-dark)', letterSpacing: '-0.01em' }}>
                Click a chart to try it in the studio
              </p>
            </div>
            <a href="#create" style={{ fontSize: 12, fontWeight: 700, color: 'var(--c2v-primary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4, opacity: 0.85 }}>
              Open Studio →
            </a>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: 12 }}>
            ...video cards...
          </div>
        </div>
      </section>
      */}

    </div>
  )
}

export default HomePage